import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { memo, useCallback } from 'react';
import { twJoin } from 'tailwind-merge';

import Icon from 'shopper/components/Icon';
import Price from 'shopper/components/Price/V2';

import Anchor from 'components/Anchor';
import ErrorBoundary from 'components/ErrorBoundary';
import Image from 'components/Image';
import ReactionGroup from 'components/ReactionGroup';
import StopWatch from 'components/StopWatch';
import Tag from 'components/Timeline/OfferCard/V2/Tag';

import useCountdown from 'hooks/useCountdown';
import useExclusiveOffer from 'hooks/useExclusiveOffer';
import useFlashOffer from 'hooks/useFlashOffer';
import useOfferRating from 'hooks/useOfferRating';

import { getShortenedTimeTo } from 'lib/date';
import { sendEvent } from 'lib/gtag';
import {
  getOfferImageLink,
  getUserAvatarUrl,
  toOfferImageAltText,
  toUserAvatarAltText,
} from 'lib/image';
import { toOfferLink } from 'lib/links';
import { toFormattedOfferOldPrice } from 'lib/offer';
import { toFormattedOfferPrice } from 'lib/offerPage';
import trackEvent from 'lib/posthog/trackEvent';

import { useUser } from 'providers/UserProvider';

import WhatsAppShareIconButton from './HomeOfferCard/WhatsAppShareIconButton';

import OFFER from 'constants/offer';

const MobileOfferCard = ({
  gaEventCategory,
  surface = null,
  offerId,
  offerComments,
  offerLikes,
  offerOldPrice = null,
  offerPhoto,
  offerPrice,
  offerPriceType,
  offerPublished,
  offerSlug,
  offerStatusName,
  offerTags = [],
  offerTitle,
  offerUserVisibility,
  storeDomain,
  storeName = null,
  userName,
  userPhoto,
}) => {
  const {
    isOfferBlocked,
    formatGaEventActionByVisibility,
    onUnloggedUserTriesOpenExclusiveOffer,
  } = useExclusiveOffer();
  const { isLoggedIn } = useUser();
  const { handleVote, currentVote, serverVote } = useOfferRating(offerId);
  const onLike = useCallback(() => handleVote('GREAT'), [handleVote]);
  const onDislike = useCallback(() => handleVote('DISLIKE'), [handleVote]);
  const flashOffer = useFlashOffer(offerId);
  const countdown = useCountdown(flashOffer?.targetDate || new Date(0));

  const isOfferExclusiveForApp =
    offerUserVisibility === OFFER.VISIBILITY_STATUS.APP_ONLY;
  const isOfferExclusiveForLoggedUsers =
    offerUserVisibility === OFFER.VISIBILITY_STATUS.LOGGED_ONLY;

  const onClick = (event) => {
    sendEvent({
      action: formatGaEventActionByVisibility(
        'click__offer',
        offerUserVisibility
      ),
      category: gaEventCategory,
      logged: isLoggedIn,
    });
    trackEvent(
      'click home offer card',
      { offer_id: offerId, store_slug: storeDomain, surface },
      { destination: 'posthog' }
    );

    if (!isOfferBlocked(offerUserVisibility, offerSlug)) {
      return;
    }

    event.preventDefault();
    onUnloggedUserTriesOpenExclusiveOffer(offerSlug, offerUserVisibility);
  };

  return (
    <NextLink href={toOfferLink(offerSlug)} prefetch={false} passHref>
      <Anchor
        className="flex h-full select-none flex-col overflow-hidden rounded-3 border-[0.5px] border-neutral-high-200 bg-neutral-high-100 dark:border-0 dark:bg-neutral-low-400"
        onClick={onClick}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-high-200 px-3 py-2 dark:border-neutral-low-200">
          <div className="flex items-center">
            <span className="mr-1 max-w-36 truncate whitespace-nowrap text-xs text-neutral-low-400 dark:text-neutral-high-200">
              {storeName || storeDomain}
            </span>
            <Icon
              className="text-neutral-high-400 dark:text-neutral-high-400"
              name="verified"
              size="size3"
            />
          </div>
          <span
            className="text-xs text-neutral-high-500 dark:text-neutral-high-400"
            suppressHydrationWarning
          >
            {getShortenedTimeTo(offerPublished)}
          </span>
        </div>

        {/* Body: imagem + conteúdo + ícones */}
        <div className="flex items-center gap-3 px-3 pb-3 pt-4">
          <Image
            alt={toOfferImageAltText(offerTitle)}
            className={twJoin(
              'h-20 w-20 shrink-0 rounded-2 object-contain',
              offerStatusName === OFFER.STATUSES.FINISHED && 'opacity-30'
            )}
            grayscale={offerStatusName === OFFER.STATUSES.FINISHED}
            height={80}
            loading="lazy"
            sizes="80px"
            src={getOfferImageLink(offerPhoto, 96)}
            width={80}
          />

          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {(offerTags.length > 0 ||
              isOfferExclusiveForApp ||
              isOfferExclusiveForLoggedUsers) && (
              <Tag
                className="w-full"
                offerTags={offerTags}
                offerUserVisibility={offerUserVisibility}
              />
            )}
            {flashOffer && (
              <ErrorBoundary>
                <StopWatch
                  hours={countdown.hours}
                  minutes={countdown.minutes}
                  phase={flashOffer.phase}
                  seconds={countdown.seconds}
                  size="xs"
                />
              </ErrorBoundary>
            )}
            <p className="line-clamp-2 text-sm font-regular text-neutral-low-400 dark:text-neutral-high-200">
              {offerTitle}
            </p>
            <div className="flex flex-wrap items-center gap-x-1">
              {offerPriceType === OFFER.PRICE_TYPES.STARTING_AT && (
                <span className="text-xs text-neutral-high-500 dark:text-neutral-high-400">
                  A partir de
                </span>
              )}
              <Price
                mobileOrientation="row"
                primaryInfo={toFormattedOfferPrice(offerPrice, offerPriceType)}
                primaryInfoClassName="!text-base"
                secondaryInfo={
                  offerPriceType === OFFER.PRICE_TYPES.STARTING_AT
                    ? null
                    : toFormattedOfferOldPrice(offerOldPrice, offerPriceType)
                }
              />
            </div>
          </div>

          <div
            className="flex shrink-0 flex-col gap-2"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {/* <AlertIconButton
              gaEventCategory={gaEventCategory}
              offerStatusName={offerStatusName}
            /> */}
            <WhatsAppShareIconButton
              offerId={offerId}
              offerPrice={offerPrice}
              offerPriceType={offerPriceType}
              offerSlug={offerSlug}
              offerTitle={offerTitle}
            />
          </div>
        </div>

        {/* Footer: avatar + username + ReactionGroup */}
        <div className="flex items-center justify-between border-t border-neutral-high-200 px-3 py-2 dark:border-neutral-low-200">
          <div className="flex min-w-0 items-center gap-1.5">
            <img
              alt={toUserAvatarAltText(userName)}
              className="h-6 w-6 shrink-0 rounded-full object-cover"
              src={getUserAvatarUrl(userPhoto, 36)}
            />
            <span className="max-w-[80px] truncate text-xs text-neutral-high-500 dark:text-neutral-high-400">
              {userName}
            </span>
          </div>
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <ReactionGroup
              commentsCount={offerComments}
              isLoggedIn={isLoggedIn}
              likes={Math.max(0, offerLikes ?? 0)}
              offerSlug={null}
              serverVote={serverVote}
              userVote={currentVote}
              onDislike={onDislike}
              onLike={onLike}
            />
          </div>
        </div>
      </Anchor>
    </NextLink>
  );
};

MobileOfferCard.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  offerComments: PropTypes.number.isRequired,
  offerId: PropTypes.number.isRequired,
  offerLikes: PropTypes.number,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerPublished: PropTypes.string.isRequired,
  offerSlug: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ),
  offerTitle: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string.isRequired,
  storeDomain: PropTypes.string.isRequired,
  storeName: PropTypes.string,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string,
};

export default memo(MobileOfferCard);
